// Sidebar.jsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import HamburgerButton from '../Components/Helpers/hamburger-button';
import DashboardIcon from "../Images/dashboard-icon.svg";
import ConversationsIcon from "../Images/conversations-icon.svg";
import CalandarIcon from "../Images/calendar-icon.svg";
import WorkspaceIcon from "../Images/workspace-icon.svg";
import PaymentsIcon from "../Images/payments.svg";
import SynthoraIcon from "../Images/synthora.svg";
import CoPilotIcon from "../Images/copilot.svg";
import LogoutIcon from "../Images/logout-icon.svg";
import { useAuth0 } from "@auth0/auth0-react";
import { isTeamMemberAuthenticated, getTeamUser, clearTeamAuth } from '../services/teamAuthService';
import axios from 'axios';

const SidebarContainer = styled.div`
  width: ${props => props.$expanded ? '6%' : '3%'};
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  border-right: 1px solid rgba(100, 150, 200, 0.15);
  transition: width 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const HamburgerContainer = styled.div`
  padding: ${props => props.$expanded ? '15px 10px' : '15px 0'};
  display: flex;
  justify-content: ${props => props.$expanded ? 'flex-start' : 'center'};
  align-items: center;
`;

const Menu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MenuItem = styled.li`
  display: flex;
  align-items: center;
  height: 50px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  justify-content: ${props => props.$expanded ? 'none' : 'center'};
`;

const MenuIcon = styled.div`
  margin: ${props => props.$expanded ? '0 5px 0 10px' : '0'};
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    width: 80%;
    height: 80%;
    filter: brightness(0) invert(1);
    opacity: 0.9;
  }
`;

const MenuText = styled.span`
  display: ${props => props.$expanded ? 'inline' : 'none'};
  color: rgba(255, 255, 255, 0.9);
  margin-left: -3px;
`;

const Sidebar = ({ expanded, onExpandToggle, isWorkspace = false }) => {
  const navigate = useNavigate();
  const { logout, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [isAdmin, setIsAdmin] = useState(true); // Default to true for backwards compatibility
  
  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated) return;
      
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER}team/members`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const members = response.data.members || [];
        
        if (members.length === 0) {
          // No team members - full admin access
          setIsAdmin(true);
        } else {
          // Team members exist - check if current user is admin
          const memberUser = getTeamUser();
          if (isTeamMemberAuthenticated() && memberUser) {
            setIsAdmin(memberUser.role === 'admin');
          } else {
            // Not yet logged in as member - will be redirected
            setIsAdmin(false);
          }
        }
      } catch (error) {
        console.error('Failed to check admin status in sidebar:', error);
        // On error, default to showing for safety
        setIsAdmin(true);
      }
    };
    
    checkAdminStatus();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Developer access (Brain Test Bench) — server-authoritative, defaults hidden.
  const [isDeveloper, setIsDeveloper] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${process.env.REACT_APP_SERVER}testbench/access`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsDeveloper(!!res.data?.isDeveloper);
      } catch { setIsDeveloper(false); }
    })();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Power mode (hand-editing of the brain in Synthora) is switched on only from the CRM; everyone else
  // builds their agent in "My agent" and sees Synthora as "Case tools" (pipelines, application review).
  const [powerMode, setPowerMode] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${process.env.REACT_APP_SERVER}profile`, { headers: { Authorization: `Bearer ${token}` } });
        setPowerMode(!!res.data?.profile?.powerMode);
      } catch { setPowerMode(false); }
    })();
  }, [isAuthenticated, getAccessTokenSilently]);

  const NavigateToPage = (where) => () => {
    navigate(where, { replace: true })
  }

  const handleLogout = () => {
    // Clear team member auth first
    clearTeamAuth();
    // Then logout from Auth0
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <SidebarContainer $expanded={expanded} $isWorkspace={isWorkspace}>
      <HamburgerContainer $expanded={expanded}>
        <HamburgerButton toggle={onExpandToggle} $isOpen={expanded} />
      </HamburgerContainer>
      
      <Menu>
        <MenuItem onClick={NavigateToPage('/')} $expanded={expanded} title="Home">
          <MenuIcon $expanded={expanded}>
            <img alt='dashboard icon' src={DashboardIcon} style={{ filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
          </MenuIcon>
          <MenuText $expanded={expanded}>Home</MenuText>
        </MenuItem>

        <MenuItem onClick={NavigateToPage('/workspace')} $expanded={expanded} title="Workspace">
          <MenuIcon $expanded={expanded}>
            <img alt='workspace icon' src={WorkspaceIcon} style={{ filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
          </MenuIcon>
          <MenuText $expanded={expanded}>Workspace</MenuText>
        </MenuItem>

        <MenuItem onClick={NavigateToPage('/calendar')} $expanded={expanded} title="Calendar">
          <MenuIcon $expanded={expanded}>
            <img alt='calendar icon' src={CalandarIcon} style={{ filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
          </MenuIcon>
          <MenuText $expanded={expanded}>Calendar</MenuText>
        </MenuItem>

        <MenuItem onClick={NavigateToPage('/payments')} $expanded={expanded} title="Payments">
          <MenuIcon $expanded={expanded}>
            <img alt='payments icon' src={PaymentsIcon} style={{ width: '95%', height: '95%', filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
          </MenuIcon>
          <MenuText $expanded={expanded}>Payments</MenuText>
        </MenuItem>

        <MenuItem onClick={NavigateToPage('/agreements')} $expanded={expanded} title="Agreements">
          <MenuIcon $expanded={expanded}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
              <path d="M8 17c1.5-2 3-3 4-1.5S17 15 16 13" />
            </svg>
          </MenuIcon>
          <MenuText $expanded={expanded}>Agreements</MenuText>
        </MenuItem>

        <MenuItem onClick={NavigateToPage('/network')} $expanded={expanded} title="Network">
          <MenuIcon $expanded={expanded}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="5" r="2.4" />
              <circle cx="5" cy="18" r="2.4" />
              <circle cx="19" cy="18" r="2.4" />
              <path d="M10.6 6.9 6.4 15.9M13.4 6.9l4.2 9M7.4 18h9.2" />
            </svg>
          </MenuIcon>
          <MenuText $expanded={expanded}>Network</MenuText>
        </MenuItem>

        {/* My agent — the agent builder (admins) */}
        {isAdmin && (
          <MenuItem onClick={NavigateToPage('/agent/build')} $expanded={expanded} title="My agent">
            <MenuIcon $expanded={expanded}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                   stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="7" width="16" height="12" rx="3" />
                <path d="M12 3v4M9 12h.01M15 12h.01M9 16h6" />
              </svg>
            </MenuIcon>
            <MenuText $expanded={expanded}>My agent</MenuText>
          </MenuItem>
        )}

        {/* Synthora - Admin Only; without Power mode it holds only the case tools */}
        {isAdmin && (
          <MenuItem onClick={NavigateToPage('/Synthora')} $expanded={expanded} title={powerMode ? 'Synthora' : 'Case tools'}>
            <MenuIcon $expanded={expanded}>
              <img alt='synthora icon' src={SynthoraIcon} style={{ width: '95%', height: '95%', filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
            </MenuIcon>
            <MenuText $expanded={expanded}>{powerMode ? 'Synthora' : 'Case tools'}</MenuText>
          </MenuItem>
        )}

        {/* Brain Test Bench - Developers Only */}
        {isDeveloper && (
          <MenuItem onClick={NavigateToPage('/test-bench')} $expanded={expanded} title="Test Bench">
            <MenuIcon $expanded={expanded}><span style={{ fontSize: 18 }}>🧪</span></MenuIcon>
            <MenuText $expanded={expanded}>Test Bench</MenuText>
          </MenuItem>
        )}

        <MenuItem onClick={NavigateToPage('/copilot')} $expanded={expanded} title="Co-Pilot">
          <MenuIcon $expanded={expanded}>
            <img alt='copilot icon' src={CoPilotIcon} style={{ width: '95%', height: '95%', filter: 'brightness(0) saturate(100%) invert(67%) sepia(74%) saturate(1000%) hue-rotate(360deg) brightness(103%) contrast(101%)' }} />
          </MenuIcon>
          <MenuText $expanded={expanded}>Co-Pilot</MenuText>
        </MenuItem>

        <MenuItem onClick={handleLogout} $expanded={expanded} title="Logout">
          <MenuIcon $expanded={expanded}>
            <img alt='logout icon' src={LogoutIcon} style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(94%) saturate(5618%) hue-rotate(355deg) brightness(100%) contrast(115%)' }} />
          </MenuIcon>
          <MenuText $expanded={expanded}>Logout</MenuText>
        </MenuItem>
      </Menu>
    </SidebarContainer>
  );
};

export default Sidebar;
